/**
 * Feature attribute editing — shared between the Edit tool panel and the
 * attribute table's own inline "quick edit."
 *
 * Originally lived entirely inside EditPanels.tsx. Pulled out so the
 * attribute table's per-row edit pencil (and its "add a field" menu) can
 * read a service's real capabilities and write a real update through the
 * exact same three paths the Edit tool already uses — a local layer's own
 * `localData`, a shared layer written through the server, or an ArcGIS
 * FeatureServer's `applyEdits` — rather than growing a second, divergent
 * copy of "is this even editable" and "how do I save one field."
 */

import { arcgisServiceUrlFor, getArcgisBaseUrl, withArcgisToken } from '../utils/arcgis';
import { computeBounds } from './localFile';
import { layerLibraryService } from '../services/layerLibraryService';
import { featureKey } from '../utils/featureKey';
import type { Layer } from '../types';

export interface EditableField {
  name: string;
  alias: string;
  type: string;
  /** Coded-value domain, when the service publishes one. */
  choices?: { code: string | number; label: string }[];
}

export interface ServiceEditInfo {
  editable: boolean;
  reason?: string;
  fields: EditableField[];
  objectIdField: string;
}

/**
 * The identity a local (file-backed) layer's fields are read under.
 *
 * A file has no OBJECTID, so the feature's own position in `localData.features`
 * is the identity instead — stable for as long as a panel or the table has it
 * open, which is all editing one record needs.
 */
export const LOCAL_ID_FIELD = '__index';

/**
 * Local files carry no schema and no service permission to check — this data
 * already lives entirely in the module (or, in the viewer, in this tab), so a
 * local layer is always "editable": there is no server that could refuse the
 * write. That holds even with zero fields, or zero features. Field TYPE is
 * inferred from the first value seen for each key, across every feature,
 * because that is all a plain FeatureCollection publishes.
 */
export function readLocalEditInfo(layer: Layer): ServiceEditInfo {
  const features: GeoJSON.Feature[] = layer.localData?.features ?? [];
  const order: string[] = [];
  const types = new Map<string, string>();
  for (const feature of features) {
    const properties = (feature?.properties ?? {}) as Record<string, unknown>;
    for (const [key, value] of Object.entries(properties)) {
      if (types.has(key)) continue;
      order.push(key);
      types.set(key, typeof value === 'number' ? 'number' : typeof value === 'boolean' ? 'boolean' : 'string');
    }
  }
  const fields: EditableField[] = order.map((name) => ({ name, alias: name, type: types.get(name) ?? 'string' }));
  return { editable: true, fields, objectIdField: LOCAL_ID_FIELD };
}

/** Turns an edited draft value back into the type its property started as, so
    a number does not quietly become the string "12" the moment it is saved. */
export function coerceLocalValue(value: unknown, type: string): unknown {
  if (type === 'boolean') return typeof value === 'boolean' ? value : String(value) === 'true';
  if (type === 'number') {
    if (typeof value === 'number') return value;
    const trimmed = String(value ?? '').trim();
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    return Number.isNaN(parsed) ? trimmed : parsed;
  }
  return value;
}

/** Reads what a service actually permits, rather than assuming it permits it. */
export async function readEditInfo(layer: Layer, arcgisUrl: string): Promise<ServiceEditInfo> {
  const none: ServiceEditInfo = { editable: false, fields: [], objectIdField: 'OBJECTID' };

  if (layer.localData) {
    return readLocalEditInfo(layer);
  }
  if (layer.arcgisLayerId === undefined) {
    return { ...none, reason: 'GeoServer WMS is a drawing service; it has no editing endpoint.' };
  }
  const effectiveUrl = arcgisServiceUrlFor(layer, { arcgisUrl });
  if (!/\/FeatureServer/i.test(effectiveUrl)) {
    return { ...none, reason: 'MapServer publishes images, not editable features. A FeatureServer can be edited.' };
  }

  const baseUrl = getArcgisBaseUrl(effectiveUrl);
  if (!baseUrl) return { ...none, reason: 'The ArcGIS service URL is not set.' };

  try {
    const response = await fetch(withArcgisToken(`${baseUrl}/${layer.arcgisLayerId}?f=json`, effectiveUrl));
    if (!response.ok) return { ...none, reason: 'The service did not answer a metadata request.' };
    const data = await response.json();
    if (data?.error) return { ...none, reason: data.error.message ?? 'The service returned an error.' };

    const capabilities = String(data.capabilities ?? '');
    if (!/update/i.test(capabilities)) {
      return {
        ...none,
        reason: `This layer is published read-only — it advertises "${capabilities || 'no capabilities'}".`,
      };
    }

    const objectIdField =
      (data.fields ?? []).find((field: any) => field?.type === 'esriFieldTypeOID')?.name ?? 'OBJECTID';

    const fields: EditableField[] = (data.fields ?? [])
      .filter(
        (field: any) =>
          field?.editable &&
          field?.type !== 'esriFieldTypeOID' &&
          field?.type !== 'esriFieldTypeGeometry' &&
          !/^(shape|shape_length|shape_area|globalid)$/i.test(field?.name ?? ''),
      )
      .map((field: any) => ({
        name: field.name,
        alias: field.alias || field.name,
        type: field.type,
        choices: field.domain?.codedValues?.map((entry: any) => ({
          code: entry.code,
          label: entry.name,
        })),
      }));

    return { editable: true, fields, objectIdField };
  } catch {
    return { ...none, reason: 'The service could not be reached.' };
  }
}

export interface AttributeSaveResult {
  success: boolean;
  message: string;
  /** For local/shared layers: apply straight to `layers` state via onLayerChange. */
  updatedLayer?: Partial<Layer>;
}

/**
 * Saves one EXISTING record's attributes. Never creates a feature and never
 * touches geometry — the attribute table's inline "quick edit" only ever
 * opens on a row that already exists, unlike the Edit tool's own save()
 * (still in EditPanels.tsx), which also handles brand-new records and a
 * reshaped geometry. Written as its own function rather than widening that
 * one, so the working Edit tool save path stays exactly as it is.
 */
export async function saveFeatureAttributesUpdate(
  layer: Layer,
  info: ServiceEditInfo,
  recordId: string,
  fields: EditableField[],
  draft: Record<string, unknown>,
  moduleId: string | undefined,
  arcgisUrl: string,
): Promise<AttributeSaveResult> {
  if (layer.localData) {
    const features = layer.localData.features ?? [];
    const index = Number(recordId);
    const original = features[index];
    if (!original) return { success: false, message: 'That feature is no longer in this layer.' };

    const updatedProperties: Record<string, unknown> = { ...(original.properties ?? {}) };
    for (const field of fields) updatedProperties[field.name] = coerceLocalValue(draft[field.name], field.type);

    if (layer.sharedLayerId) {
      if (!moduleId) return { success: false, message: 'This shared layer has no module to save through.' };
      try {
        const updated = await layerLibraryService.updateSharedLayerFeature(
          moduleId,
          layer.id,
          index,
          updatedProperties,
          null,
        );
        return {
          success: true,
          message: 'Saved — visible to everyone this layer is shared with.',
          updatedLayer: {
            localData: updated.localData,
            bounds: updated.localData ? computeBounds(updated.localData) : undefined,
          },
        };
      } catch (error) {
        return { success: false, message: error instanceof Error ? error.message : 'Could not save that change.' };
      }
    }

    const updatedFeatures = features.slice();
    updatedFeatures[index] = { ...original, properties: updatedProperties };
    const updatedData: GeoJSON.FeatureCollection = { ...layer.localData, features: updatedFeatures };
    return {
      success: true,
      message: 'Saved.',
      updatedLayer: { localData: updatedData, bounds: computeBounds(updatedData) },
    };
  }

  const effectiveUrl = arcgisServiceUrlFor(layer, { arcgisUrl });
  const baseUrl = getArcgisBaseUrl(effectiveUrl);
  if (!baseUrl || layer.arcgisLayerId === undefined) {
    return { success: false, message: 'This layer has no editable service.' };
  }

  const attributes: Record<string, unknown> = {};
  for (const field of fields) attributes[field.name] = draft[field.name] ?? null;
  attributes[info.objectIdField] = Number(recordId);

  try {
    const body = new URLSearchParams({ f: 'json', updates: JSON.stringify([{ attributes }]) });
    const response = await fetch(
      withArcgisToken(`${baseUrl}/${layer.arcgisLayerId}/applyEdits`, effectiveUrl),
      { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() },
    );
    const data = response.ok ? await response.json() : null;
    const result = data?.updateResults?.[0];
    if (result?.success) return { success: true, message: 'Saved to the service.' };
    return {
      success: false,
      message: result?.error?.description || data?.error?.message || 'The service refused the edit.',
    };
  } catch {
    return { success: false, message: 'The service could not be reached.' };
  }
}

export interface NewFieldDef {
  name: string;
  alias: string;
  type: 'string' | 'number' | 'boolean';
}

export interface AddFieldResult {
  success: boolean;
  message: string;
  updatedLayer?: Partial<Layer>;
}

/**
 * Adds a new attribute field to a layer's schema.
 *
 * Local layers: there is no separate schema to change, only the features
 * themselves — the new field is written onto every existing feature's
 * properties (with a type-appropriate default) so it reads back exactly
 * like a field that had always been there, per readLocalEditInfo's own
 * scan-every-feature discovery. A SHARED local layer is refused here rather
 * than attempted: doing this properly means writing every feature back
 * through the server one at a time (there is no bulk-update endpoint), which
 * for a few hundred features is a real, slow, partially-failable operation —
 * a deliberate scope boundary, not an oversight.
 *
 * ArcGIS FeatureServer: attempted via the service's own `addToDefinition`
 * admin operation. Most FeatureServer connections this app holds a plain
 * edit token for do NOT have schema-edit privilege — expect this to be
 * refused by the service far more often than the plain attribute save above,
 * and it is surfaced the same honest way every other service refusal is.
 */
export async function addFieldToLayer(
  layer: Layer,
  field: NewFieldDef,
  arcgisUrl: string,
): Promise<AddFieldResult> {
  if (layer.localData) {
    if (layer.sharedLayerId) {
      return {
        success: false,
        message:
          'Adding a field to a shared layer isn’t supported yet — it would mean rewriting every feature on the server one at a time. Add fields before sharing this layer, or ask whoever owns the source data to add it there.',
      };
    }
    const defaultValue = field.type === 'number' ? null : field.type === 'boolean' ? false : '';
    const features = (layer.localData.features ?? []).map((feature) => {
      const properties = (feature.properties ?? {}) as Record<string, unknown>;
      if (field.name in properties) return feature;
      return { ...feature, properties: { ...properties, [field.name]: defaultValue } };
    });
    const updatedData: GeoJSON.FeatureCollection = { ...layer.localData, features };
    return {
      success: true,
      message: `Added "${field.alias || field.name}".`,
      updatedLayer: { localData: updatedData },
    };
  }

  const effectiveUrl = arcgisServiceUrlFor(layer, { arcgisUrl });
  const baseUrl = getArcgisBaseUrl(effectiveUrl);
  if (!baseUrl || layer.arcgisLayerId === undefined) {
    return { success: false, message: 'This layer has no editable service.' };
  }

  const esriType =
    field.type === 'number'
      ? 'esriFieldTypeDouble'
      : field.type === 'boolean'
        ? 'esriFieldTypeSmallInteger' // ArcGIS has no native boolean; stored 0/1.
        : 'esriFieldTypeString';
  const newField: Record<string, unknown> = {
    name: field.name,
    type: esriType,
    alias: field.alias || field.name,
    nullable: true,
    editable: true,
  };
  if (esriType === 'esriFieldTypeString') newField.length = 255;

  try {
    const body = new URLSearchParams({
      f: 'json',
      addToDefinition: JSON.stringify({ fields: [newField] }),
    });
    const response = await fetch(
      withArcgisToken(`${baseUrl}/${layer.arcgisLayerId}/addToDefinition`, effectiveUrl),
      { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() },
    );
    const data = response.ok ? await response.json() : null;
    if (data?.error) {
      return {
        success: false,
        message:
          data.error.message ||
          'The service refused to add this field — most connections do not have schema-edit permission.',
      };
    }
    return { success: true, message: `Added "${field.alias || field.name}" to the service.` };
  } catch {
    return { success: false, message: 'The service could not be reached.' };
  }
}

/** One row's own computed value, ready to write — the Field Calculator's
 *  output for one record. */
export interface FieldCalculatorWrite {
  /** `AttributeFeature.id` — already the right identity for an ArcGIS layer
   *  (its own OBJECTID, which `f=geojson` already sets as the GeoJSON
   *  feature's `id`). Unused for a local layer; see `key` for that case. */
  id: string;
  /** `AttributeFeature.key` (`featureKey`) — the identity that survives a
   *  local layer's own filtering, the same one `saveFeatureAttributesUpdate`'s
   *  own caller (the attribute table's single-row save) already has to
   *  re-resolve to a TRUE array index before saving, for the exact same
   *  reason: `record.id` is this row's position in the FILTERED set the
   *  table is currently showing, not its real position in
   *  `layer.localData.features`. */
  key?: string;
  value: unknown;
}

export interface BulkCalculateResult {
  success: boolean;
  message: string;
  /** For local/shared layers: apply straight to `layers` state via onLayerChange. */
  updatedLayer?: Partial<Layer>;
  /** Writes that did NOT make it, by their own `id`/`key` (whichever the
   *  caller used to build `writes`) — empty on full success. An ArcGIS
   *  batch can partially fail (most OIDs saved, a few refused); a local
   *  layer either writes every row in one pass or fails the whole batch. */
  failedIds: Set<string>;
}

/** ArcGIS `applyEdits`' own `updates` array has no documented hard cap, but
 *  a single request holding thousands of attribute sets is still worth
 *  paging — the same reasoning TracePanel's own PAGE_SIZE already applies
 *  to reads, just smaller: a write that fails partway through a huge single
 *  POST is harder to diagnose than one failed page out of several. */
const ARCGIS_BATCH_SIZE = 200;

/**
 * Writes ONE field's calculated value across MANY rows at once — Field
 * Calculator's own save path, built beside (not inside) the single-record
 * `saveFeatureAttributesUpdate` above because the efficient shape genuinely
 * differs per layer type: a local layer wants ONE new `features` array and
 * ONE `onLayerChange`, not N; an ArcGIS layer wants `applyEdits`' own
 * batching, not N separate POSTs. Both still end up writing through the
 * exact same two real paths (`layer.localData`, or the service's own
 * `applyEdits`) `saveFeatureAttributesUpdate` uses for one row.
 */
export async function calculateField(
  layer: Layer,
  targetField: EditableField,
  writes: FieldCalculatorWrite[],
  /** `ServiceEditInfo.objectIdField` — the real OID field name for THIS
   *  service (not always literally "OBJECTID"), already resolved by
   *  `readEditInfo` before this is ever called. Unused for a local layer. */
  objectIdField: string,
  moduleId: string | undefined,
  arcgisUrl: string,
): Promise<BulkCalculateResult> {
  if (writes.length === 0) return { success: true, message: 'Nothing to calculate.', failedIds: new Set() };

  if (layer.localData) {
    const features = layer.localData.features ?? [];

    if (layer.sharedLayerId) {
      if (!moduleId) {
        return {
          success: false,
          message: 'This shared layer has no module to save through.',
          failedIds: new Set(writes.map((write) => write.key ?? write.id)),
        };
      }
      // No bulk-update endpoint for a shared layer (see addFieldToLayer's
      // own doc comment above) — each row is its own request, same
      // documented cost/slowness tradeoff already accepted there.
      const failedIds = new Set<string>();
      let lastLocalData: GeoJSON.FeatureCollection | undefined;
      for (const write of writes) {
        const index = features.findIndex((feature) => featureKey(feature, layer.id) === write.key);
        if (index === -1) {
          failedIds.add(write.key ?? write.id);
          continue;
        }
        try {
          // This endpoint REPLACES a feature's properties wholesale, the
          // same reason the single-row save above spreads the original
          // properties first rather than sending just the one changed
          // field — sending only `targetField` here would wipe out every
          // other attribute on the feature.
          const updatedProperties = {
            ...(features[index].properties ?? {}),
            [targetField.name]: coerceLocalValue(write.value, targetField.type),
          };
          const updated = await layerLibraryService.updateSharedLayerFeature(
            moduleId,
            layer.id,
            index,
            updatedProperties,
            null,
          );
          if (updated.localData) lastLocalData = updated.localData;
        } catch {
          failedIds.add(write.key ?? write.id);
        }
      }
      return {
        success: failedIds.size < writes.length,
        message:
          failedIds.size === 0
            ? 'Saved — visible to everyone this layer is shared with.'
            : `Saved ${writes.length - failedIds.size} of ${writes.length} rows — the rest could not be saved.`,
        updatedLayer: lastLocalData
          ? { localData: lastLocalData, bounds: computeBounds(lastLocalData) }
          : undefined,
        failedIds,
      };
    }

    // Plain local layer: one pass, one new array, one onLayerChange — no
    // network round trip per row the way the shared-layer branch needs.
    const byKey = new Map<string, FieldCalculatorWrite>();
    for (const write of writes) if (write.key) byKey.set(write.key, write);

    const failedIds = new Set<string>(writes.map((write) => write.key ?? write.id));
    const updatedFeatures = features.map((feature) => {
      const key = featureKey(feature, layer.id);
      const write = key ? byKey.get(key) : undefined;
      if (!write) return feature;
      failedIds.delete(write.key ?? write.id);
      return {
        ...feature,
        properties: {
          ...(feature.properties ?? {}),
          [targetField.name]: coerceLocalValue(write.value, targetField.type),
        },
      };
    });
    const updatedData: GeoJSON.FeatureCollection = { ...layer.localData, features: updatedFeatures };
    return {
      success: failedIds.size === 0,
      message:
        failedIds.size === 0
          ? `Calculated ${writes.length} row${writes.length === 1 ? '' : 's'}.`
          : `Calculated ${writes.length - failedIds.size} of ${writes.length} rows — the rest were no longer in this layer.`,
      updatedLayer: { localData: updatedData, bounds: computeBounds(updatedData) },
      failedIds,
    };
  }

  const effectiveUrl = arcgisServiceUrlFor(layer, { arcgisUrl });
  const baseUrl = getArcgisBaseUrl(effectiveUrl);
  if (!baseUrl || layer.arcgisLayerId === undefined) {
    return {
      success: false,
      message: 'This layer has no editable service.',
      failedIds: new Set(writes.map((write) => write.id)),
    };
  }

  const failedIds = new Set<string>();
  for (let offset = 0; offset < writes.length; offset += ARCGIS_BATCH_SIZE) {
    const batch = writes.slice(offset, offset + ARCGIS_BATCH_SIZE);
    const updates = batch.map((write) => ({
      attributes: { [objectIdField]: Number(write.id), [targetField.name]: write.value ?? null },
    }));
    try {
      const body = new URLSearchParams({ f: 'json', updates: JSON.stringify(updates) });
      const response = await fetch(
        withArcgisToken(`${baseUrl}/${layer.arcgisLayerId}/applyEdits`, effectiveUrl),
        { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() },
      );
      const data = response.ok ? await response.json() : null;
      const results: { objectId?: number; success?: boolean }[] = data?.updateResults ?? [];
      if (results.length === 0) {
        // The service answered, but not with a per-row result for this
        // batch (a connection error, or an error applying to the whole
        // batch) — every row in it is unaccounted for, so all fail rather
        // than silently reporting the rest of the run as successful.
        for (const write of batch) failedIds.add(write.id);
        continue;
      }
      for (const result of results) {
        if (!result.success) failedIds.add(String(result.objectId ?? ''));
      }
    } catch {
      for (const write of batch) failedIds.add(write.id);
    }
  }

  return {
    success: failedIds.size === 0,
    message:
      failedIds.size === 0
        ? `Calculated ${writes.length} row${writes.length === 1 ? '' : 's'}.`
        : `Calculated ${writes.length - failedIds.size} of ${writes.length} rows — the service refused the rest.`,
    failedIds,
  };
}
